import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Drawer,
  Form,
  Select,
  Input,
  Button,
  DatePicker,
  Collapse,
  Switch,
  Divider,
  message,
  Space,
  Spin,
  Modal,
} from 'antd';
import moment from 'moment';
import {
  getEmployees,
  getOffices,
  getTemplatePatterns,
  getCurrentPattern,
  getSaveAccommodationSuccess,
  getSaveLoading,
  getCurrentTemplatePattern,
  getRCDByLocationNumber,
  getSaveAccommodationFailureMssg,
} from './selectors';
import { useMsal } from '@azure/msal-react';
import {
  saveTemplateAccommodation,
  fetchCurrentTemplatePatterns,
  fetchRCDByLocationNumber,
  resetSaveAccommodationSuccess,
} from './actions';
import { set, template } from 'lodash';
import { showErrorNotification } from '@med-fe/util';
import { fetchChangeResults } from './sagas';

const { Panel } = Collapse;
const { TextArea } = Input;

interface AddTemplateDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (values: any) => void;
  loggedInUser?: string;
  loggedInEmail?: string;
  ods?: Array<{
    employeeNumber: string;
    name: string;
    id: string;
    locations: Array<{
      locationNumber: string;
      locationName: string;
    }>;
  }>;
  offices?: Array<{ name: string; odName: string; locationNumber: string; locationName: string }>;
  templates?: Array<string>; // Changed to array of strings
  dailyTemplates?: Array<string>; // Changed to array of strings
  changeVettedOptions?: Array<{ label: string; value: string }>;
  requestReasonOptions?: Record<string, Array<{ label: string; value: string }>>;
  rcdLookup?: string;
  modalData?: any;
}

const AddTemplateDrawer: React.FC<AddTemplateDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  loggedInUser = '',
  loggedInEmail: initialLoggedInEmail = '',
  ods = [],
  offices = [],
  templates = [],
  dailyTemplates = [],
  changeVettedOptions = [],
  requestReasonOptions = {},
  rcdLookup = '',
  modalData = undefined,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [selectedOD, setSelectedOD] = useState<string | null>(null);
  const [scope, setScope] = useState('All Days');
  const [changeResult, setChangeResult] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDuplicateAllowed, setIsDuplicateAllowed] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);
  const { accounts } = useMsal();
  offices = useSelector(getOffices);
  ods = useSelector(getEmployees);
  templates = useSelector(getTemplatePatterns);
  const currentPattern = useSelector(getCurrentTemplatePattern);
  const rcd = useSelector(getRCDByLocationNumber);
  loggedInUser = accounts[0]?.name || loggedInUser;
  const loggedInEmail = accounts[0]?.username || initialLoggedInEmail;
  const [requestReason, setRequestReason] = useState<string[]>([]);
  const saveLoading = useSelector(getSaveLoading);
  const saveAccommodationSuccess = useSelector(getSaveAccommodationSuccess);
  const saveAccommodationFailureMssg = useSelector(getSaveAccommodationFailureMssg);
  const duplicateModalShownRef = useRef(false);
  const lastFailureKeyRef = useRef<string | null>(null);

  const handleChangeResult = (val) => {
    setChangeResult(val);
    form.setFieldsValue({ requestReason: undefined, notes: undefined }); // reset dependent fields
    setRequestReason([]);
  };
  const requestReasonOptionsMap = {
    Reduction: [
      { label: 'Space Restriction', value: 'Space Restriction' },
      { label: 'Staff Restriction', value: 'Staff Restriction' },
      { label: 'OD Accommodation', value: 'OD Accommodation' },
      { label: 'Other', value: 'Other' },
    ],
    'No Change': [{ label: 'Other', value: 'Other' }],
    Addition: [{ label: 'Other', value: 'Other' }],
  };

  const [patternUpdateKey, setPatternUpdateKey] = useState(0);

  const handleNewPattern = async (day, newPattern) => {
    try {
      const currentValue = form.getFieldValue('changeResult');
      const allPatterns = [];

      if (scope === 'All Days') {
        const payload = { currentPattern, newPattern };
        const result = await fetchChangeResults(payload);

        form.setFieldsValue({ changeResult: result });
      } else if (scope === 'Specific Days') {
        // Manually clear and set to force update in form
        form.setFieldsValue({ [`dailyPattern${day}`]: undefined });

        // Little delay to simulate value clearing and resetting
        form.setFieldsValue({ [`dailyPattern${day}`]: newPattern });
        setPatternUpdateKey((prev) => prev + 1); // force rerender

        const allPatterns = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
          .map((d) => form.getFieldValue(`dailyPattern${d}`))
          .filter(Boolean);

        console.log('Updated patterns:', allPatterns);

        const currentPattern = form.getFieldValue('currentPattern');
        // const allPatterns = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
        //   .map(d => form.getFieldValue(`dailyPattern${d}`))
        //   .filter(Boolean);

        const results = await Promise.all(
          allPatterns.map((p) => fetchChangeResults({ currentPattern, newPattern: p }))
        );

        let finalChangeResult = 'No Change';
        if (results.includes('Reduction')) {
          finalChangeResult = 'Reduction';
        } else if (results.includes('Addition')) {
          finalChangeResult = 'Addition';
        }

        form.setFieldsValue({ changeResult: finalChangeResult });
        form.setFieldsValue({ requestReason: requestReasonOptionsMap[finalChangeResult] });
      }
    } catch (error) {
      showErrorNotification('Error comparing patterns');
    }
  };

  const handleODChange = (od: string) => {
    setSelectedOD(od);
    // Clear current pattern when provider changes
    form.setFieldsValue({ currentPattern: '', rcd: '', locations: [] });

    // Extract employee number from the selected provider
    const extractEmployeeNumber = (providerValue: string) => {
      if (!providerValue) return null;
      const match = providerValue.match(/\(([^)]+)\)$/);
      return match ? match[1] : providerValue;
    };

    const employeeNumber = extractEmployeeNumber(od);

    // Find the selected OD and get their locations
    const selectedODData = ods.find((odItem) => odItem.employeeNumber === employeeNumber);

    if (selectedODData?.locations && selectedODData.locations.length > 0) {
      // Prepopulate locations from the selected OD
      const locationValues = String(selectedODData.locations[0].locationNumber);
      form.setFieldsValue({ locations: [locationValues] });

      // Call APIs with the first location
      if (locationValues.length > 0) {
        callFetchCurrentTemplatePatterns(od, [locationValues]);
        callFetchRCDByLocationNumber([locationValues]);
        form.setFieldsValue({ rcd: rcd?.name ?? '' });
      }
    }
  };

  const handleLocationChange = (locations: string[]) => {
    // Clear current pattern when location changes
    form.setFieldsValue({ currentPattern: '' });
    form.setFieldsValue({ rcd: '' });

    const currentProvider = form.getFieldValue('od');
    if (currentProvider && locations && locations.length > 0) {
      callFetchCurrentTemplatePatterns(currentProvider, locations);
      callFetchRCDByLocationNumber(locations);
      form.setFieldsValue({ rcd: rcd?.name ?? 'Default Name' });
    }
  };

  const callFetchCurrentTemplatePatterns = async (provider: string, locations: string[]) => {
    const extractEmployeeNumber = (providerValue: string) => {
      const match = providerValue.match(/\(([^)]+)\)$/);
      return match ? match[1] : providerValue;
    };

    const extractLocationNumbers = (locationValues: string[]) => {
      return locationValues.map((location) => {
        const match = location.match(/\(([^)]+)\)$/);
        return match ? match[1] : location;
      });
    };

    const employeeNumber = extractEmployeeNumber(provider);
    const locationNumbers = extractLocationNumbers(locations);

    if (employeeNumber && locationNumbers.length > 0) {
      try {
        const result: any = await dispatch(
          fetchCurrentTemplatePatterns({
            employeeNumber,
            locationNumber: locationNumbers[0],
          })
        );

        if (result) {
          form.setFieldsValue({ currentPattern: currentPattern });
        } else {
          form.setFieldsValue({ currentPattern: '' });
        }
      } catch (error) {
        console.error('Error fetching current pattern:', error);
        form.setFieldsValue({ currentPattern: '' });
      }
    }
  };

  const callFetchRCDByLocationNumber = (locations: string[]) => {
    // Extract location numbers from locations
    const extractLocationNumbers = (locationValues: string[]) => {
      if (!Array.isArray(locationValues)) return [];
      return locationValues.map((location) => {
        const match = location.match(/\(([^)]+)\)$/);
        return match ? match[1] : location;
      });
    };

    const locationNumbers = extractLocationNumbers(locations);

    if (locationNumbers.length > 0) {
      form.setFieldsValue({ rcd: '' });
      dispatch(
        fetchRCDByLocationNumber({
          locationNumber: locationNumbers[0], // Pass first location number
        })
      );
    }
  };

  const handleSubmit = async (values: any, overrideDuplicate = false) => {
    try {
      setIsSubmitting(true);
      setIsDuplicateAllowed(false);
      duplicateModalShownRef.current = false;
      lastFailureKeyRef.current = null;

      // Use the override flag to decide the effective duplicate permission
      const effectiveIsDuplicateAllowed = overrideDuplicate || isDuplicateAllowed;

      // Extract employee number from provider selection (format: "Name (EMP123)")
      const extractEmployeeNumber = (providerValue: string) => {
        if (!providerValue) return null;
        const match = providerValue.match(/\(([^)]+)\)$/);
        return match ? match[1] : providerValue;
      };

      // Extract location numbers from location selections (format: "Name (LOC123)")
      const extractLocationNumbers = (locationValues: string[]) => {
        if (!Array.isArray(locationValues)) return [];
        return locationValues.map((location) => {
          const match = location.match(/\(([^)]+)\)$/);
          return match ? match[1] : location;
        });
      };

      const templateData = {
        requestSid: requestId ?? 0,
        employeeNumber: values.od,
        locations: values.locations,
        currentPattern: values.currentPattern || null,
        mondayPattern: scope === 'Specific Days' ? values.dailyPatternMonday : null,
        tuesdayPattern: scope === 'Specific Days' ? values.dailyPatternTuesday : null,
        wednesdayPattern: scope === 'Specific Days' ? values.dailyPatternWednesday : null,
        thursdayPattern: scope === 'Specific Days' ? values.dailyPatternThursday : null,
        fridayPattern: scope === 'Specific Days' ? values.dailyPatternFriday : null,
        saturdayPattern: scope === 'Specific Days' ? values.dailyPatternSaturday : null,
        sundayPattern: scope === 'Specific Days' ? values.dailyPatternSunday : null,
        newPattern: scope === 'All Days' ? values.newPattern : null,
        templateStartDate: values.startDate ? values.startDate.toISOString() : null,
        templateEndDate: values.endDate ? values.endDate.toISOString() : null,
        addressedWith: Array.isArray(values.vettedWith) ? values.vettedWith.join(', ') : values.vettedWith,
        rcd: modalData?.Rcd || rcd?.email || null,
        slotReductionAddition: values.changeResult,
        requestReason: Array.isArray(values.requestReason) ? values.requestReason.join(', ') : values.requestReason,
        notes: values.notes || null,
        requestedBy: loggedInUser,
        requestedByEmail: loggedInEmail,
        isDuplicateAllowed: effectiveIsDuplicateAllowed,
      };

      // Dispatch save action to saga
      dispatch(saveTemplateAccommodation(templateData));
    } catch (error) {
      console.error('Error submitting template accommodation:', error);
      message.error('Failed to submit template accommodation request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    setPatternUpdateKey((prev) => prev + 1);
  }, [scope, templates]);

  useEffect(() => {
    if (saveAccommodationSuccess) {
      form.resetFields();
      setSelectedOD(null);
      setScope('All Days');
      setChangeResult(null);
      onClose();
      dispatch(resetSaveAccommodationSuccess());
    }
    setShowDuplicateWarning(false);
  }, [saveAccommodationSuccess]);

  useEffect(() => {
    form.setFieldsValue({ rcd: rcd?.name ?? '' });
  }, [rcd]);

  useEffect(() => {
    if (isSubmitting) return;

    const messages = saveAccommodationFailureMssg?.error?.messages ?? [];
    // Normalize messages to avoid minor differences (whitespace/casing) causing
    // different keys for the same logical failure.
    const normalizedMessages = messages.map((m) => String(m).trim().toLowerCase());
    const alreadyBooked = normalizedMessages.some((m) =>
      m.includes('appointments are already booked for this location and provider within these dates.')
    );

    // Build a stable key for this failure payload so repeated identical failures
    // won't re-open the modal (covers dev StrictMode double-mounts or duplicate
    // reducer updates).
    const failureKey = alreadyBooked && normalizedMessages.length > 0 ? normalizedMessages.join('|') : null;

    if (alreadyBooked && !isDuplicateAllowed) {
      if (failureKey && lastFailureKeyRef.current !== failureKey && !duplicateModalShownRef.current) {
        lastFailureKeyRef.current = failureKey;
        duplicateModalShownRef.current = true; // mark that modal has been shown for this failureKey
        //console.log('Showing duplicate modal for failureKey:', failureKey);
        setShowDuplicateWarning(true);
      } else {
        //console.log('Skipping duplicate modal; failureKey already seen or modal already shown:', failureKey);
      }
    }

    if (saveAccommodationSuccess === true) {
      // reset sentinel on success so future failures show again
      setShowDuplicateWarning(false);
      setIsDuplicateAllowed(false);
    }
  }, [saveAccommodationFailureMssg, saveAccommodationSuccess, isDuplicateAllowed, isSubmitting]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setSelectedOD(null);
      setScope('All Days');
      setChangeResult(null);
      setIsSubmitting(false);
    } else {
      dispatch(resetSaveAccommodationSuccess());
      // Initialize form when drawer opens
      const initialFields: any = {
        requestedBy: loggedInUser,
        rcd: rcdLookup,
        scope: 'All Days',
      };

      if (modalData) {
        setRequestId(modalData.RequestId || null);
        if (modalData.OD) initialFields.od = `${modalData.OD} (${modalData.EmployeeNumber})`;

        if (modalData.Offices && Array.isArray(offices)) {
          let officeNumbers: string[] = [];
          if (Array.isArray(modalData.Offices)) {
            officeNumbers = modalData.Offices;
          } else if (typeof modalData.Offices === 'string') {
            officeNumbers = modalData.Offices.split(',').map((s: string) => s.trim());
          }
          initialFields.locations = officeNumbers
            .map((locNum: string) => {
              const found = offices.find(
                (o: any) => o.locationNumber === locNum || o.locationNumber === String(locNum)
              );
              return found ? `${found.locationName} (${found.locationNumber})` : locNum;
            })
            .filter(Boolean);
        }

        if (modalData.CurrentPattern) {
          initialFields.currentPattern = modalData.CurrentPattern;
        }

        if (modalData.NewPattern) {
          initialFields.newPattern = modalData.NewPattern;
          initialFields.scope = 'All Days';
          setScope('All Days');
        }
        if (modalData.NewPattern === null) {
          initialFields.scope = 'Specific Days';
          setScope('Specific Days');
        }
        if (modalData.StartDate) initialFields.startDate = moment(modalData.StartDate);
        if (modalData.EndDate) initialFields.endDate = modalData.EndDate ? moment(modalData.EndDate) : null;
        if (modalData.Requestor) initialFields.requestedBy = modalData.Requestor;
        if (modalData.ChangeResult) {
          initialFields.changeResult = modalData.ChangeResult;
          setChangeResult(modalData.ChangeResult);
        }
        // ...existing code for daily patterns, addressedWith, requestReason, notes, rcd, etc...
        if (modalData.MondayPattern) initialFields.dailyPatternMonday = modalData.MondayPattern;
        if (modalData.TuesdayPattern) initialFields.dailyPatternTuesday = modalData.TuesdayPattern;
        if (modalData.WednesdayPattern) initialFields.dailyPatternWednesday = modalData.WednesdayPattern;
        if (modalData.ThursdayPattern) initialFields.dailyPatternThursday = modalData.ThursdayPattern;
        if (modalData.FridayPattern) initialFields.dailyPatternFriday = modalData.FridayPattern;
        if (modalData.SaturdayPattern) initialFields.dailyPatternSaturday = modalData.SaturdayPattern;
        if (modalData.SundayPattern) initialFields.dailyPatternSunday = modalData.SundayPattern;
        if (modalData.AddressedWith) {
          initialFields.vettedWith = Array.isArray(modalData.AddressedWith)
            ? modalData.AddressedWith
            : String(modalData.AddressedWith)
                .split(',')
                .map((v: string) => v.trim());
        }
        if (modalData.RequestReason) {
          initialFields.requestReason = Array.isArray(modalData.RequestReason)
            ? modalData.RequestReason
            : String(modalData.RequestReason)
                .split(',')
                .map((v: string) => v.trim());
          setRequestReason(modalData.RequestReason);
        }
        if (modalData.Notes) initialFields.notes = modalData.Notes;
        if (modalData.Rcd) initialFields.rcd = modalData.Rcd;

        // Optionally map other fields as needed
        if (modalData.vettedWith)
          initialFields.vettedWith = Array.isArray(modalData.vettedWith)
            ? modalData.vettedWith
            : String(modalData.vettedWith)
                .split(',')
                .map((v: string) => v.trim());
        if (modalData.requestedBy) initialFields.requestedBy = modalData.requestedBy;
        if (modalData.rcd) initialFields.rcd = modalData.rcd;
        if (modalData.changeResult) initialFields.changeResult = modalData.changeResult;
        if (modalData.requestReason)
          initialFields.requestReason = Array.isArray(modalData.requestReason)
            ? modalData.requestReason
            : String(modalData.requestReason)
                .split(',')
                .map((v: string) => v.trim());
        setRequestReason(modalData.requestReason);
        if (modalData.notes) initialFields.notes = modalData.notes;
      }

      form.setFieldsValue(initialFields);

      // If modalData.OD is present, set selectedOD for dependent fields
      if (modalData?.OD) setSelectedOD(modalData.OD);
      // If modalData.RequestReason or requestReason is present, set requestReason state
      if (modalData?.RequestReason) {
        setRequestReason(
          Array.isArray(modalData.RequestReason)
            ? modalData.RequestReason
            : String(modalData.RequestReason)
                .split(',')
                .map((v: string) => v.trim())
        );
      } else if (modalData?.requestReason) {
        setRequestReason(
          Array.isArray(modalData.requestReason)
            ? modalData.requestReason
            : String(modalData.requestReason)
                .split(',')
                .map((v: string) => v.trim())
        );
      }
    }
  }, [open, form, loggedInUser, rcdLookup, modalData, offices]);

  useEffect(() => {
    if (currentPattern) {
      form.setFieldsValue({ currentPattern });
    }
  }, [currentPattern, rcd, form]);

  return (
    <>
      <Drawer
        title={modalData ? 'EDIT TEMPLATE ACCOMMODATION REQUEST' : 'ADD NEW TEMPLATE ACCOMMODATION REQUEST'}
        placement='right'
        width={600}
        onClose={onClose}
        open={open}
        closable={false}
        className='dc-drawer-panel'
        forceRender={true}
        extra={
          <Space>
            <Button
              danger
              onClick={onClose}
              style={{
                backgroundColor: '#d14d4d',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                paddingLeft: '8px',
                paddingRight: '8px',
                fontSize: '14px',
                height: '27px',
                fontWeight: 'bold',
              }}
            >
              Cancel
            </Button>
            <Button
              type='primary'
              loading={isSubmitting}
              disabled={isSubmitting}
              onClick={() => form.submit()}
              style={{
                backgroundColor: '#036',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                paddingLeft: '8px',
                paddingRight: '8px',
                fontSize: '14px',
                height: '27px',
                fontWeight: 'bold',
              }}
            >
              Submit
            </Button>
          </Space>
        }
      >
        <div className=''>
          <Form
            layout='vertical'
            form={form}
            onFinish={handleSubmit}
            initialValues={{
              changeResult: 'No Change',
            }}
            onValuesChange={() => {}}
          >
            <Form.Item label='Provider' name='od' rules={[{ required: true, message: 'Please select a Provider' }]}>
              <Select
                showSearch
                placeholder='Select Provider'
                onChange={handleODChange}
                options={ods?.map(({ id, employeeNumber, name }) => {
                  return {
                    key: id,
                    value: `${name} (${employeeNumber})`,
                    label: `${name} (${employeeNumber})`,
                  };
                })}
                optionFilterProp='label'
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Form.Item>

            <Form.Item
              label='Location'
              name='locations'
              rules={[{ required: true, message: 'Please select Location' }]}
            >
              <Select
                allowClear
                placeholder='Select location'
                showSearch
                disabled={!selectedOD}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                value={form.getFieldValue('locations') || undefined}
                onChange={(selected: string) => {
                  form.setFieldsValue({ locations: [selected] });
                  handleLocationChange([selected]);
                }}
                options={
                  selectedOD
                    ? (() => {
                        const employeeNumber = selectedOD.match(/\(([^)]+)\)$/)?.[1] ?? selectedOD;
                        const selectedODData = ods.find((odItem) => odItem.employeeNumber === employeeNumber);
                        return (
                          selectedODData?.locations?.map(({ locationNumber, locationName }) => ({
                            label: `${locationName} (${locationNumber})`,
                            value: String(locationNumber),
                            key: locationNumber,
                          })) || []
                        );
                      })()
                    : []
                }
              />
            </Form.Item>
            <Form.Item label='Current Pattern' name='currentPattern'>
              <Input disabled placeholder='Auto-filled from PACE' />
            </Form.Item>

            <Form.Item label='Scope of Request' name='scope'>
              <Switch
                checkedChildren='All Days'
                unCheckedChildren='Specific Days'
                checked={scope === 'All Days'}
                onChange={(checked) => {
                  const newScope = checked ? 'All Days' : 'Specific Days';
                  setScope(newScope);

                  if (newScope === 'All Days') {
                    form.setFields([
                      { name: 'dailyPatternMonday', errors: [] },
                      { name: 'dailyPatternTuesday', errors: [] },
                      { name: 'dailyPatternWednesday', errors: [] },
                      { name: 'dailyPatternThursday', errors: [] },
                      { name: 'dailyPatternFriday', errors: [] },
                      { name: 'dailyPatternSaturday', errors: [] },
                      { name: 'dailyPatternSunday', errors: [] },
                    ]);
                  } else {
                    form.setFields([
                      { name: 'dailyPatternMonday', errors: [] },
                      { name: 'dailyPatternTuesday', errors: [] },
                      { name: 'dailyPatternWednesday', errors: [] },
                      { name: 'dailyPatternThursday', errors: [] },
                      { name: 'dailyPatternFriday', errors: [] },
                      { name: 'dailyPatternSaturday', errors: [] },
                      { name: 'dailyPatternSunday', errors: [] },
                    ]);
                  }
                }}
              />
            </Form.Item>

            {scope === 'All Days' ? (
              <Form.Item
                label='New Pattern'
                name='newPattern'
                rules={[{ required: true, message: 'Please select a template' }]}
              >
                <Select
                  placeholder='Select template'
                  options={templates?.map((t) => ({ label: t, value: t }))}
                  onChange={(value) => {
                    handleNewPattern('All', value);
                  }}
                  getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
              </Form.Item>
            ) : (
              ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                <Form.Item
                  key={`${day}-${patternUpdateKey}`}
                  label={`Daily Pattern (${day})`}
                  name={`dailyPattern${day}`}
                  rules={[
                    {
                      validator: async (_, value) => {
                        if (scope === 'Specific Days') {
                          const allDailyPatterns = [
                            form.getFieldValue('dailyPatternMonday'),
                            form.getFieldValue('dailyPatternTuesday'),
                            form.getFieldValue('dailyPatternWednesday'),
                            form.getFieldValue('dailyPatternThursday'),
                            form.getFieldValue('dailyPatternFriday'),
                            form.getFieldValue('dailyPatternSaturday'),
                            form.getFieldValue('dailyPatternSunday'),
                          ];
                          const hasAtLeastOne = allDailyPatterns.some((p) => p && p.trim() !== '');
                          if (!hasAtLeastOne) {
                            return Promise.reject(new Error('At least one daily pattern must be selected'));
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    key={`${day}-${patternUpdateKey}`}
                    allowClear
                    placeholder={`Select pattern for ${day}`}
                    options={templates?.map((t) => ({ label: t, value: t }))}
                    onChange={(value) => handleNewPattern(day, value)}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                  />
                </Form.Item>
              ))
            )}

            <Form.Item
              label='Template Start Date'
              name='startDate'
              rules={[
                { required: true, message: 'Please select start date' },
                {
                  validator: (_, value) => {
                    if (value && value.isBefore(moment().startOf('day'))) {
                      return Promise.reject(new Error('Start date cannot be in the past'));
                    }
                    const endDate = form.getFieldValue('endDate');
                    if (value && endDate && value.isAfter(endDate)) {
                      return Promise.reject(new Error('Start date should not be after end date'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => d && d < moment().startOf('day')}
                onChange={() => form.validateFields(['endDate'])}
                getPopupContainer={(trigger: HTMLElement) => trigger.parentElement as HTMLElement}
              />
            </Form.Item>

            <Form.Item
              label='Template End Date'
              name='endDate'
              rules={[
                {
                  validator: (_, value) => {
                    if (value && value.isBefore(moment().startOf('day'))) {
                      return Promise.reject(new Error('End date cannot be in the past'));
                    }
                    const startDate = form.getFieldValue('startDate');
                    if (value && startDate && value.isBefore(startDate)) {
                      return Promise.reject(new Error('End date should not be before start date'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(d) => d && d < moment().startOf('day')}
                onChange={() => form.validateFields(['startDate'])}
                getPopupContainer={(trigger: HTMLElement) => trigger.parentElement as HTMLElement}
              />
            </Form.Item>

            <Form.Item
              label='Change Vetted With'
              name='vettedWith'
              rules={[{ required: true, message: 'Please select at least one option' }]}
            >
              <Select
                mode='multiple'
                placeholder='Select options'
                options={[
                  { label: 'OD', value: 'OD' },
                  { label: 'GM', value: 'GM' },
                  { label: 'DM', value: 'DM' },
                  { label: 'Other', value: 'Other' },
                ]}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Form.Item>

            <Form.Item
              label='Requested By'
              name='requestedBy'
              rules={[{ required: true, message: 'Please select a user' }]}
            >
              <Select
                showSearch
                placeholder='Search and select user'
                disabled
                defaultValue={loggedInUser}
                options={ods?.map(({ employeeNumber, name }) => ({
                  label: `${name} (${employeeNumber})`,
                  value: `${name} (${employeeNumber})`,
                  key: employeeNumber,
                }))}
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Form.Item>

            <Form.Item label='RCD' name='rcd'>
              <Input value={rcd?.name ?? ''} disabled />
            </Form.Item>

            <Form.Item
              label='Change Results'
              name='changeResult'
              rules={[{ required: true, message: 'Change result is required' }]}
            >
              <Select
                placeholder='Select change result'
                options={[
                  { label: 'No Change', value: 'No Change' },
                  { label: 'Addition', value: 'Addition' },
                  { label: 'Reduction', value: 'Reduction' },
                ]}
                disabled
                onChange={() => form.setFieldsValue({ requestReason: [] })}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Form.Item>

            <Form.Item shouldUpdate={(prev, curr) => prev.changeResult !== curr.changeResult}>
              {({ getFieldValue }) => {
                const currentChangeResult = getFieldValue('changeResult');

                return (
                  <Form.Item
                    label='Request Reason'
                    name='requestReason'
                    rules={[
                      {
                        required: true,
                        message: 'Request reason is required',
                      },
                    ]}
                  >
                    <Select
                      mode='multiple'
                      placeholder='Select request reason'
                      options={requestReasonOptionsMap[currentChangeResult] || []}
                      onChange={(val) => setRequestReason(val)}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>

            <Form.Item
              label='Notes'
              name='notes'
              rules={[
                {
                  validator: (_, value) => {
                    if (requestReason.includes('Other') && (!value || !value.trim())) {
                      return Promise.reject(new Error('Please enter notes if "Other" is selected'));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TextArea rows={3} placeholder='Enter notes...' />
            </Form.Item>

            <Divider />
          </Form>
        </div>
      </Drawer>
      <Modal
        title={null}
        centered
        width={1200}
        style={{ maxWidth: 'none', width: 1200, borderRadius: '6px' }}
        bodyStyle={{ padding: 24, width: '100%' }}
        open={showDuplicateWarning}
        modalRender={(modal) => <div style={{ width: 700 }}>{modal}</div>}
        onCancel={() => {
          duplicateModalShownRef.current = false;
          lastFailureKeyRef.current = null;
          setShowDuplicateWarning(false);
        }}
        zIndex={2000}
        footer={
          <div>
            <Button
              danger
              size='middle'
              onClick={() => {
                // User declined to submit duplicate; allow modal to be shown again for future attempts
                duplicateModalShownRef.current = false;
                lastFailureKeyRef.current = null;
                setShowDuplicateWarning(false);
              }}
              style={{
                fontSize: '12px',
                backgroundColor: '#d14d4d',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                height: '27px',
                fontWeight: 'bold',
                minWidth: '80px',
              }}
            >
              No
            </Button>
            <Button
              type='primary'
              size='middle'
              onClick={() => {
                // User confirmed they want to submit despite duplicate.
                // Keep the sentinel true until we get success (which will reset lastFailureKeyRef and allow future modals).
                setIsDuplicateAllowed(true);
                // Submit after a short delay to allow modal to close
                setTimeout(() => form.submit(), 100);
              }}
              style={{
                fontSize: '12px',
                backgroundColor: '#036',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                height: '27px',
                fontWeight: 'bold',
                minWidth: '80px',
              }}
            >
              Yes
            </Button>
          </div>
        }
      >
        <div
          style={{
            backgroundColor: '#036',
            color: 'white',
            padding: '10px 16px',
            fontWeight: 'bold',
            fontSize: '16px',
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            margin: '-24px -24px 24px',
          }}
        >
          Submit Duplicate Accommodation Request
        </div>

        <span>
          Warning: Appointments are already booked for this location and provider within these dates. Do you still want
          to submit this request?
        </span>
      </Modal>
    </>
  );
};

export default AddTemplateDrawer;
